
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getSignedUrl } from "@/lib/storage";

// Add this to extend the jsPDF type with autoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable: {
      finalY: number;
    };
  }
}

export const generateWorkoutPDF = async (workout: any) => {
  try {
    // Buscar informações adicionais do treino
    const { data: workoutData, error: workoutError } = await supabase
      .from("workouts")
      .select(`
        *,
        students(*),
        target_student:students(first_name, last_name)
      `)
      .eq("id", workout.id)
      .single();

    if (workoutError) throw workoutError;

    // Buscar informações do personal trainer
    const { data: trainerData, error: trainerError } = await supabase
      .from("profiles")
      .select("first_name, last_name")
      .eq("id", workout.trainer_id)
      .single();

    if (trainerError) throw trainerError;

    // Fetch exercises for this workout
    const { data: exercises, error: exercisesError } = await supabase
      .from("exercises")
      .select("*")
      .eq("workout_id", workout.id)
      .order("day", { ascending: true })
      .order("order_index", { ascending: true });

    if (exercisesError) throw exercisesError;

    // Create PDF document
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.setTextColor("#9B87F5");
    doc.text(workoutData.title, 20, 20);

    // Add header information in a table format
    const headerData = [
      ['Tipo', workoutData.type || 'N/A'],
      ['Duração', `${workoutData.duration || 'N/A'} minutos`],
      ['Nível', workoutData.difficulty_level || 'N/A'],
    ];

    if (workoutData.target_student) {
      headerData.unshift(['Aluno', `${workoutData.target_student.first_name} ${workoutData.target_student.last_name}`]);
    }

    if (trainerData) {
      headerData.push(['Personal Trainer', `${trainerData.first_name} ${trainerData.last_name}`]);
    }

    // Add the header table
    doc.autoTable({
      startY: 30,
      head: [],
      body: headerData,
      theme: 'plain',
      styles: {
        fontSize: 11,
        cellPadding: 3,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 'auto' }
      }
    });

    let currentY = doc.lastAutoTable.finalY + 10;

    // Add description if available
    if (workoutData.description) {
      doc.setFontSize(12);
      doc.setTextColor("#333333");
      doc.text("Descrição:", 20, currentY);
      doc.setFontSize(10);
      const descriptionLines = doc.splitTextToSize(workoutData.description, 170);
      doc.text(descriptionLines, 20, currentY + 7);
      currentY += 7 + (descriptionLines.length * 5) + 10;
    }

    // Group exercises by day
    const exercisesByDay = exercises?.reduce((acc: any, exercise: any) => {
      if (!acc[exercise.day]) {
        acc[exercise.day] = [];
      }
      acc[exercise.day].push(exercise);
      return acc;
    }, {});

    // Add exercises for each day
    if (exercisesByDay) {
      for (const [day, dayExercises] of Object.entries(exercisesByDay)) {
        // Add new page if not enough space
        if (currentY > 250) {
          doc.addPage();
          currentY = 20;
        }

        // Add day header
        doc.setFontSize(16);
        doc.setTextColor("#9B87F5");
        doc.text(`Dia ${day}`, 20, currentY);
        currentY += 10;

        // Create table for exercises
        const exerciseRows = [];
        for (const exercise of (dayExercises as any[])) {
          exerciseRows.push([
            exercise.name,
            `${exercise.sets} séries x ${exercise.reps} ${exercise.weight ? `@ ${exercise.weight}` : ''}`,
            exercise.notes || ''
          ]);
        }

        doc.autoTable({
          startY: currentY,
          head: [['Exercício', 'Séries/Reps', 'Notas']],
          body: exerciseRows,
          theme: 'grid',
          styles: {
            fontSize: 10,
            cellPadding: 5
          },
          columnStyles: {
            0: { cellWidth: 70 },
            1: { cellWidth: 50 },
            2: { cellWidth: 'auto' }
          },
          headStyles: {
            fillColor: "#9B87F5",
            textColor: "#FFFFFF",
            fontStyle: 'bold'
          }
        });

        currentY = doc.lastAutoTable.finalY + 15;
      }
    }

    // Add notes if available
    if (workoutData.notes) {
      if (currentY > 250) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(12);
      doc.setTextColor("#333333");
      doc.text("Observações:", 20, currentY);
      doc.setFontSize(10);
      const notesLines = doc.splitTextToSize(workoutData.notes, 170);
      doc.text(notesLines, 20, currentY + 7);
    }

    // Save the PDF
    const fileName = `${workoutData.title.toLowerCase().replace(/\s+/g, "-")}.pdf`;
    doc.save(fileName);
    
    toast.success("PDF do treino gerado com sucesso!");
  } catch (error) {
    console.error("Erro ao gerar PDF:", error);
    toast.error("Erro ao gerar o PDF do treino. Por favor, tente novamente.");
    throw error;
  }
};
